import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  ShoppingCart, 
  DollarSign,
  HeadphonesIcon,
  Package,
  TrendingUp
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ icon: Icon, title, value, subtitle, color = '#467322' }) => (
  <Card className="bg-white border border-[#467322]/20 hover:shadow-lg transition-all duration-200 nutrigym-card-hover">
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-600 font-medium mb-1">{title}</p>
          <p className="text-3xl font-bold text-black" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            {value}
          </p>
          {subtitle && (
            <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
          )}
        </div>
        <div 
          className="p-3 rounded-lg"
          style={{ backgroundColor: `${color}15` }}
        >
          <Icon size={24} style={{ color }} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const ClientDashboard = () => {
  const { t } = useLanguage();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/client-stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#467322] border-t-transparent" />
      </div>
    );
  }

  const statsData = [
    { 
      icon: ShoppingCart, 
      title: t('myOrders'), 
      value: stats?.my_orders || 0,
      color: '#467322'
    },
    { 
      icon: DollarSign, 
      title: t('totalSpent'), 
      value: `$${(stats?.total_spent || 0).toLocaleString()}`,
      color: '#6CA33E'
    },
    { 
      icon: HeadphonesIcon, 
      title: 'Mis Tickets', 
      value: stats?.my_tickets || 0,
      color: '#3A601C'
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="client-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-[#467322]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            {t('dashboard')}
          </h1>
          <p className="text-gray-600 mt-1">{t('overview')} - Cliente</p>
        </div>
        <div className="flex items-center gap-2 text-[#467322]">
          <TrendingUp size={20} />
          <span className="text-sm font-medium">En tiempo real</span>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {statsData.map((stat, index) => (
          <StatCard key={stat.label} {...stat} />
        ))}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Orders Summary */}
        <Card className="bg-[#467322] border-none">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              <ShoppingCart size={24} />
              Mis Pedidos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center p-6 bg-white/10 rounded-lg">
              <p className="text-5xl font-bold text-white">{stats?.my_orders || 0}</p>
              <p className="text-white/80 mt-2">Pedidos Realizados</p>
            </div>
          </CardContent>
        </Card>

        {/* Spending Summary */}
        <Card className="bg-white border border-[#467322]/20">
          <CardHeader>
            <CardTitle className="text-[#467322] flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              <DollarSign size={24} />
              Resumen de Gastos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-[#F2F7F0] rounded-lg">
                <span className="text-gray-700">{t('totalSpent')}</span>
                <span className="text-2xl font-bold text-[#467322]">
                  ${(stats?.total_spent || 0).toLocaleString()}
                </span>
              </div>
              <div className="flex items-center justify-between p-4 bg-[#F2F7F0] rounded-lg">
                <span className="text-gray-700">Tickets de Soporte</span>
                <span className="text-2xl font-bold text-[#467322]">
                  {stats?.my_tickets || 0}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className="bg-white border border-[#467322]/20">
        <CardHeader>
          <CardTitle className="text-[#467322] flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            <Package size={24} />
            Acciones Rápidas
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <a href="/client/supplements" className="p-4 border border-[#467322]/20 rounded-lg hover:bg-[#F2F7F0] transition-colors text-center">
              <Package className="mx-auto mb-2 text-[#467322]" size={32} />
              <p className="font-semibold text-black">Ver Suplementos</p>
            </a>
            <a href="/client/orders" className="p-4 border border-[#467322]/20 rounded-lg hover:bg-[#F2F7F0] transition-colors text-center">
              <ShoppingCart className="mx-auto mb-2 text-[#467322]" size={32} />
              <p className="font-semibold text-black">Mis Pedidos</p>
            </a>
            <a href="/client/support" className="p-4 border border-[#467322]/20 rounded-lg hover:bg-[#F2F7F0] transition-colors text-center">
              <HeadphonesIcon className="mx-auto mb-2 text-[#467322]" size={32} />
              <p className="font-semibold text-black">Soporte</p>
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ClientDashboard;
